import PageTitle from '@/components/PageTitle';
import AuthLayout from '@/layouts/AuthLayout';
import clsx from 'clsx';
import { Link, useForm, usePage } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';

type PageProps = {
    token_valid: boolean;
};

const ResetPassword = ({ token_valid }: PageProps) => {
    const { t } = useTranslation();
    const page = usePage();
    const actionUrl = page.url ?? '/';

    const { data, setData, post, errors, processing } = useForm<{
        password: string;
        password_confirmation: string;
    }>({
        password: '',
        password_confirmation: '',
    });

    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        post(actionUrl);
    };

    if (!token_valid) {
        return (
            <AuthLayout>
                <PageTitle title="Restablecer Contrasena" />
                <div className="w-full text-center">
                    <h1 className="mb-3 text-2xl font-semibold">
                        {t('auth.resetPassword.invalidTitle')}
                    </h1>
                    <p className="text-sm text-gray-500">
                        {t('auth.resetPassword.invalidDescription')}
                    </p>
                    <div className="mt-6 flex flex-col gap-3">
                        <Link
                            href="/auth/forgot-password/"
                            className="text-sm font-medium text-gray-900 hover:underline"
                        >
                            {t('auth.resetPassword.requestNew')}
                        </Link>
                        <Link
                            href="/auth/login/"
                            className="text-sm text-gray-500 hover:underline"
                        >
                            {t('auth.resetPassword.backToLogin')}
                        </Link>
                    </div>
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout>
            <PageTitle title="Restablecer Contrasena" />
            <div className="w-full">
                <div className="mb-6 text-center">
                    <h1 className="mb-3 text-2xl font-semibold">
                        {t('auth.resetPassword.title')}
                    </h1>
                    <p className="text-sm text-gray-500">
                        {t('auth.resetPassword.description')}
                    </p>
                </div>

                <form onSubmit={onSubmit}>
                    <div className="flex flex-col gap-y-5">
                        <div>
                            <label
                                htmlFor="password"
                                className="mb-1.5 block text-sm font-medium text-gray-700"
                            >
                                {t('auth.resetPassword.password')}
                            </label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={data.password}
                                onChange={(e) => setData('password', e.target.value)}
                                placeholder="••••••••"
                                className={clsx(
                                    'w-full rounded-md border px-3 py-2 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2',
                                    errors.password
                                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                                        : 'border-gray-300 focus:border-gray-400 focus:ring-gray-200'
                                )}
                            />
                            {errors.password && (
                                <p className="mt-1 text-xs text-red-500">
                                    {errors.password}
                                </p>
                            )}
                        </div>

                        <div>
                            <label
                                htmlFor="password_confirmation"
                                className="mb-1.5 block text-sm font-medium text-gray-700"
                            >
                                {t('auth.resetPassword.confirmPassword')}
                            </label>
                            <input
                                type="password"
                                id="password_confirmation"
                                name="password_confirmation"
                                value={data.password_confirmation}
                                onChange={(e) =>
                                    setData('password_confirmation', e.target.value)
                                }
                                placeholder="••••••••"
                                className={clsx(
                                    'w-full rounded-md border px-3 py-2 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2',
                                    errors.password_confirmation
                                        ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                                        : 'border-gray-300 focus:border-gray-400 focus:ring-gray-200'
                                )}
                            />
                            {errors.password_confirmation && (
                                <p className="mt-1 text-xs text-red-500">
                                    {errors.password_confirmation}
                                </p>
                            )}
                        </div>

                        {errors.token && (
                            <div className="rounded border border-red-200 bg-red-50 p-2.5">
                                <p className="text-xs text-red-600">
                                    {errors.token}
                                </p>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={processing}
                            className="inline-flex w-full items-center justify-center gap-x-2 rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {processing && (
                                <svg
                                    aria-hidden="true"
                                    role="status"
                                    className="inline h-4 w-4 animate-spin text-white opacity-75"
                                    viewBox="0 0 100 101"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                        fill="currentColor"
                                    ></path>
                                    <path
                                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                        fill="white"
                                    ></path>
                                </svg>
                            )}
                            {processing
                                ? t('auth.resetPassword.loading')
                                : t('auth.resetPassword.submit')}
                        </button>

                        <div className="pt-1 text-center">
                            <Link
                                href="/auth/login/"
                                className="text-sm font-medium text-gray-900 hover:underline"
                            >
                                {t('auth.resetPassword.backToLogin')}
                            </Link>
                        </div>
                    </div>
                </form>
            </div>
        </AuthLayout>
    );
};

export default ResetPassword;
